import {
  AtSign,
  BarChart3,
  Bell,
  CalendarCheck,
  CalendarClock,
  CalendarPlus,
  CalendarX,
  Clock,
  FolderKanban,
  ListChecks,
  Mail,
  Timer,
  UserPlus,
  Users,
} from 'lucide-react';
import { ROUTES } from '../constants/routes';
import { parseApiDate } from './time';

/**
 * Pure helpers for notification display (icon, tone, route, time).
 * Kept separate from the bell so they can be unit-tested without mounting the tree.
 */

const TONES = {
  primary: 'bg-primary-50 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300',
  sky: 'bg-sky-50 text-sky-600 dark:bg-sky-900/40 dark:text-sky-300',
  violet: 'bg-violet-50 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300',
  amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300',
  green: 'bg-green-50 text-green-600 dark:bg-green-900/40 dark:text-green-300',
  rose: 'bg-rose-50 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300',
  neutral: 'bg-secondary-100 text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300',
};

const TYPE_META = {
  project_assignment: { icon: FolderKanban, tone: 'primary' },
  task_assignment: { icon: ListChecks, tone: 'sky' },
  task_mention: { icon: AtSign, tone: 'violet' },
  approval_request: { icon: UserPlus, tone: 'amber' },
  leave_request: { icon: CalendarClock, tone: 'amber' },
  leave_submitted: { icon: CalendarPlus, tone: 'primary' },
  leave_approved: { icon: CalendarCheck, tone: 'green' },
  leave_declined: { icon: CalendarX, tone: 'rose' },
  weekly_hours_reminder: { icon: Timer, tone: 'amber' },
  weekly_hours_lead_summary: { icon: Users, tone: 'violet' },
  weekly_hours_hr_report: { icon: BarChart3, tone: 'primary' },
  time_entry_created: { icon: Clock, tone: 'green' },
  personal: { icon: Mail, tone: 'primary' },
};

const DEFAULT_META = { icon: Bell, tone: 'neutral' };

const RESOLUTIONS = {
  approved: { label: 'Approved', status: 'Completed' },
  declined: { label: 'Declined', colorClass: TONES.rose },
};

export function getNotificationIcon(type) {
  return (TYPE_META[type] || DEFAULT_META).icon;
}

export function getNotificationTone(type) {
  return TONES[(TYPE_META[type] || DEFAULT_META).tone];
}

/** StatusPill props for an approval request an admin already acted on, else null. */
export function getNotificationResolution(notif) {
  if (!notif?.resolution) return null;
  return RESOLUTIONS[notif.resolution] || { label: 'Closed', status: 'Not Started' };
}

export function getNotificationRoute(notif) {
  if (!notif) return null;
  if (notif.related_type === 'task' && notif.related_id) {
    return `/tasks/${notif.related_id}`;
  }
  if (notif.related_type === 'project' && notif.related_id) {
    return `/projects/${notif.related_id}`;
  }
  if (notif.related_type === 'leave') return ROUTES.LEAVE_MANAGEMENT;
  if (notif.type === 'approval_request') return ROUTES.TEAM;
  if (notif.type === 'weekly_hours_reminder') return ROUTES.TIMESHEET;
  if (
    notif.type === 'weekly_hours_lead_summary' ||
    notif.type === 'weekly_hours_hr_report'
  ) {
    return ROUTES.RESOURCE_UTILIZATION;
  }
  if (notif.type === 'time_entry_created') return ROUTES.TIME_ENTRIES;
  if (notif.type === 'personal' || notif.source === 'admin') return ROUTES.INBOX;
  return null;
}

/** Compact relative time for list rows: "Just now", "5m ago", "3h ago", "2d ago", "Nov 3". */
export function formatNotificationTime(value, now = new Date()) {
  const date = parseApiDate(value);
  if (!date) return '';
  const minutes = Math.floor(Math.max(0, now.getTime() - date.getTime()) / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', {
    timeZone: 'Asia/Karachi',
    month: 'short',
    day: 'numeric',
  });
}

/** Full PKT timestamp for tooltips. */
export function formatNotificationTimestamp(value) {
  const date = parseApiDate(value);
  if (!date) return '';
  return date.toLocaleString('en-US', {
    timeZone: 'Asia/Karachi',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatBadgeCount(count) {
  return count > 99 ? '99+' : String(count);
}
